/**
 * Cliente WebSocket para /ws/live.
 *
 * Uma única conexão por aba (singleton em módulo). Reconecta com
 * backoff exponencial 1s → 2s → 4s → 8s → 16s → 30s. Responde `pong`
 * a cada `ping` do servidor para manter o heartbeat.
 *
 * Exceção: fechamento com código 1008 suspende a conexão e pergunta a
 * `/api/auth/me` (via `setLiveSessionChecker`) o que ele significou. Com
 * 401 vira terminal (`unauthorized`); com 200 ou com falha de rede,
 * reconecta — ver `CODIGO_SESSAO_INVALIDA`.
 *
 * API pública:
 *   useLiveEvent(type, handler)   — subscreve callback para um tipo
 *   subscribeLive(type, handler)  — o mesmo, fora do ciclo de hooks
 *   useLiveConnection()           — { status, lastEventAt }
 *   setLiveSessionChecker(f)      — como saber se a sessão ainda vale
 *
 * Design: como o hook é usado em várias telas ao mesmo tempo, a
 * conexão precisa ser compartilhada. Um singleton em escopo de módulo
 * carrega essa responsabilidade — cada uso de `useLiveEvent`
 * acrescenta um listener na fila; cada `useLiveConnection` registra
 * um subscriber de estado.
 */
import { useEffect, useRef, useState } from 'react';

// ------ Tipos ------
export type LiveEvent =
  | { type: 'transaction.started'; payload: { txId: number; cpId: string; connectorId: number; idTag: string; startTime: string } }
  | { type: 'transaction.ended'; payload: { txId: number; cpId: string; energyWh: number; stopReason: string | null; stopTime: string } }
  | { type: 'transaction.metervalue'; payload: { txId: number; cpId: string; energyWh: number; powerW: number; soc?: number; ts: string } }
  | { type: 'charger.status'; payload: { cpId: string; connectorId: number; status: string; ts: string } }
  | { type: 'alert.created'; payload: { id: number; cpId: string | null; level: 'info' | 'warning' | 'error'; message: string; createdAt: string } }
  | { type: 'system.audit'; payload: { action: string; target: string | null; targetId: string | null; userEmail: string | null; createdAt: string } };

export type LiveEventType = LiveEvent['type'];
export type LiveEventPayload<T extends LiveEventType> = Extract<LiveEvent, { type: T }>['payload'];
/**
 * `verifying` é o intervalo em que o cliente recebeu 1008 e ainda NÃO sabe
 * o que ele significou — `/api/auth/me` está em voo. Não pode ser `open`
 * (o socket morreu) nem `unauthorized` (ainda não se sabe).
 *
 * `unauthorized` é TERMINAL, e só se chega nele com 1008 **confirmado por
 * 401**. Ver `tratarFechamentoPorSessao`.
 */
export type LiveConnStatus =
  | 'connecting'
  | 'open'
  | 'reconnecting'
  | 'closed'
  | 'verifying'
  | 'unauthorized';

/**
 * O que `/api/auth/me` respondeu depois de um 1008.
 *
 *  - `valida`        — 200. A sessão continua de pé; o 1008 foi "suas
 *                      concessões mudaram, reabra o canal".
 *  - `invalida`      — 401. A sessão morreu de verdade.
 *  - `indeterminada` — nem um nem outro (rede caiu, 500, DNS). Não autoriza
 *                      afirmação nenhuma sobre a sessão.
 *
 * Para o canal, `valida` e `indeterminada` têm o MESMO efeito (reconectar).
 * A distinção existe porque o `AuthProvider` precisa dela: em `invalida`
 * ele derruba o usuário; em `indeterminada` não pode derrubar ninguém por
 * causa de uma queda de rede momentânea.
 */
export type ResultadoSessaoLive = 'valida' | 'invalida' | 'indeterminada';

// ------ Singleton de conexão ------

type AnyHandler = (payload: unknown, ts: string) => void;

const handlers = new Map<LiveEventType, Set<AnyHandler>>();
type ConnListener = (s: { status: LiveConnStatus; lastEventAt: number | null }) => void;
const statusListeners = new Set<ConnListener>();

let ws: WebSocket | null = null;
let status: LiveConnStatus = 'closed';
let lastEventAt: number | null = null;
let backoffMs = 1000;
let closedByUser = false;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let refs = 0;
/**
 * Código com que o servidor fecha o canal quando o cliente não pode
 * continuar com as credenciais que apresentou no handshake.
 *
 * **1008 significa duas coisas hoje, e o cliente não consegue distinguir.**
 * No servidor (`auth.ts`, `users-routes.ts`, `revalidateSessions`) o mesmo
 * código sai em quatro gatilhos, e só UM deles mata a sessão:
 *
 * | gatilho                      | sessão | o que o servidor quer |
 * |------------------------------|--------|-----------------------|
 * | `active: false`              | morta  | que o cliente saia    |
 * | mudança de `role`            | VIVA   | que o cliente REABRA  |
 * | mudança de `garageScope`     | VIVA   | que o cliente REABRA  |
 * | `globalGarageAccess` cai     | VIVA   | que o cliente REABRA  |
 *
 * Nos três últimos derrubar o socket é o *mecanismo de atualização* das
 * concessões — e ele depende de o cliente reconectar. Tratar 1008 como
 * terminal seco deixava a aba dizendo "sessão encerrada" com a sessão
 * perfeitamente válida, os badges ainda atualizando por HTTP ao lado, e
 * só o F5 como saída.
 *
 * Por isso 1008 **suspende** a conexão e dispara uma pergunta a
 * `/api/auth/me`; o 401 é que torna a coisa terminal. A separação correta
 * (dois códigos distintos) é do servidor e vem na entrega do
 * `/ws/dashboard`.
 *
 * `1001` (servidor indo dormir) e `1006` (queda anormal, sem handshake de
 * fecho) continuam reconectando direto — são falhas de transporte, e o
 * backoff existe exatamente para elas.
 */
const CODIGO_SESSAO_INVALIDA = 1008;

/**
 * Trava de reconexão. Ligada no 1008 (provisória, enquanto se pergunta) e
 * mantida só se a resposta for 401. Solta em três lugares, todos por
 * consequência e nenhum por botão: resposta não-401, `releaseConnection`
 * com `refs` em zero, ou ausência de verificador.
 */
let sessaoInvalida = false;

/**
 * Prazo da verificação de sessão.
 *
 * Promessa pendurada **não rejeita**: sem prazo, um `/api/auth/me` que o
 * proxy aceita e nunca responde deixaria o canal em `verifying` para
 * sempre — mais um estado terminal sem rearme, que é o defeito que esta
 * feature já cometeu uma vez.
 *
 * 10s de propósito, o mesmo valor de `TIMEOUT_LEITURA_MS` no
 * `use-nav-badges.ts`: são as duas idas à rede desta feature, e dois
 * números diferentes exigiriam explicar uma diferença que não existe. Não
 * importo a constante de lá porque a dependência corre no sentido oposto
 * (`use-nav-badges` importa este módulo) e o import fecharia um ciclo.
 *
 * Menos que isso encurtaria demais: rede lenta responderia
 * `indeterminada`, o canal reabriria e tomaria outro 1008 — um laço de
 * pergunta a cada poucos segundos. O estouro não afirma nada sobre a
 * sessão; só devolve `indeterminada`, que reconecta.
 */
const TIMEOUT_VERIFICACAO_MS = 10_000;

/**
 * Serializa as verificações. Incrementado a cada 1008 e em
 * `releaseConnection`; o resultado de uma verificação só vale se o ciclo
 * ainda for o dele. Sem isto, um logout ocorrido COM a pergunta em voo
 * seria seguido de uma reconexão que ninguém pediu.
 */
let cicloSessao = 0;

/**
 * Pergunta "essa sessão ainda vale?" a quem sabe responder.
 *
 * Existe porque o singleton vive fora da árvore React e não pode usar
 * `useAuth()`. Quem registra é o `AuthProvider`: ele bate em
 * `/api/auth/me`, atualiza o usuário com as concessões NOVAS quando volta
 * 200, derruba para o login quando volta 401, e não afirma nada quando a
 * pergunta nem chegou.
 */
let verificarSessao: (() => Promise<ResultadoSessaoLive> | ResultadoSessaoLive) | null = null;

/** Registra (ou remove, com `null`) o verificador de sessão. */
export function setLiveSessionChecker(
  fn: (() => Promise<ResultadoSessaoLive> | ResultadoSessaoLive) | null,
): void {
  verificarSessao = fn;
}

function setStatus(s: LiveConnStatus) {
  status = s;
  const snap = { status, lastEventAt };
  for (const l of statusListeners) l(snap);
}

function emit<T extends LiveEventType>(type: T, payload: LiveEventPayload<T>, ts: string) {
  lastEventAt = Date.now();
  const set = handlers.get(type);
  if (!set) return;
  for (const h of set) {
    try { h(payload as unknown, ts); } catch { /* ignore */ }
  }
}

function scheduleReconnect() {
  if (closedByUser) return;
  // Defesa em profundidade: HOJE esta linha é inalcançável com a trava
  // ligada — os dois únicos caminhos até aqui são o `catch` do
  // `new WebSocket` (que não roda, porque `connect()` retorna antes) e o
  // ramo não-1008 do `close` (que não roda, porque com a trava ligada não
  // nasce socket novo). **Não é coberta por teste**; está aqui porque
  // custa uma linha e porque um caminho novo até `scheduleReconnect` não
  // pode furar a trava por esquecimento. Não conte como testada.
  if (sessaoInvalida) return;
  if (reconnectTimer) return;
  setStatus('reconnecting');
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect();
    backoffMs = Math.min(30_000, backoffMs * 2);
  }, backoffMs);
}

function connect() {
  if (ws) return;
  // Com a trava ligada ninguém reabre o canal por conta própria — nem a
  // tela que monta no meio da verificação. Quem destrava é o resultado da
  // pergunta (ou o fim do ciclo de sessão), nunca o consumidor.
  if (sessaoInvalida) return;
  closedByUser = false;
  setStatus(status === 'closed' ? 'connecting' : status);
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const url = `${proto}//${window.location.host}/ws/live`;
  let socket: WebSocket;
  try {
    socket = new WebSocket(url);
  } catch {
    scheduleReconnect();
    return;
  }
  ws = socket;
  setStatus('connecting');

  // Todos os handlers abaixo comparam `ws !== socket` antes de tocar em
  // qualquer estado compartilhado. É a mesma guarda que a camada OCPP já
  // aplica em `apps/server/src/ocpp.ts` (`ws.on('close')`): *só mexe no
  // registro se ESSE socket ainda é o registrado*, senão o `close`
  // atrasado de um socket antigo apaga a referência ao socket vivo.
  //
  // Aqui a sequência que quebrava era `refs` 1→0→1 sem sair do tick:
  // `releaseConnection` marca `closedByUser` e fecha o socket A (ainda em
  // CONNECTING); `connect()` devolve `closedByUser` para `false` e cria o
  // socket B; o `close` atrasado de A chegava com `closedByUser === false`,
  // zerava `ws` (que já era B) e agendava reconexão — B ficava aberto e
  // fora do alcance de `releaseConnection`, respondendo `pong` ao heartbeat
  // do servidor para sempre.
  socket.addEventListener('open', () => {
    // Socket que abre DEPOIS de ter sido substituído (ou depois do logout)
    // é órfão: ninguém tem referência para fechá-lo e o servidor nunca vai
    // derrubá-lo, porque o heartbeat dele só mede `pong`. Fecha na hora e
    // não publica status — quem manda no status é o socket corrente.
    if (ws !== socket) { try { socket.close(); } catch { /* ignore */ } return; }
    backoffMs = 1000;
    setStatus('open');
  });
  socket.addEventListener('close', (ev) => {
    // Guarda de identidade: sem ela, o `close` de um socket já substituído
    // apagava `ws` (apontando para o socket VIVO) e agendava reconexão.
    if (ws !== socket) return;
    ws = null;
    if (closedByUser) { setStatus('closed'); return; }
    // O `code` vinha sendo descartado (o handler nem recebia o evento), e
    // com ele a única diferença entre "a rede caiu" e "a sua sessão acabou".
    // Sem essa distinção, sessão revogada virava reconexão de 30 em 30s
    // para sempre, calada: o `contar()` do menu também toma 401, o menu
    // diz "sem sinal", e nada convida a entrar de novo.
    if (ev.code === CODIGO_SESSAO_INVALIDA) {
      tratarFechamentoPorSessao();
      return;
    }
    scheduleReconnect();
  });
  socket.addEventListener('error', () => {
    // "error" precede "close" — deixamos o close cuidar do reconnect.
    // Não há guarda de identidade aqui porque este handler não toca em
    // estado compartilhado nenhum (`ws`, `status`, `backoffMs`,
    // `reconnectTimer`). Se algum dia passar a tocar, a MESMA guarda
    // (`if (ws !== socket) return;`) tem de vir antes de tudo.
  });
  socket.addEventListener('message', (ev) => {
    // Socket substituído não fala pelo canal: não entrega evento a handler
    // nenhum e, sobretudo, não responde `pong`. Era o `pong` do órfão que
    // fazia o heartbeat de 45s do servidor nunca derrubá-lo.
    if (ws !== socket) { try { socket.close(); } catch { /* ignore */ } return; }
    try {
      const parsed = JSON.parse(String(ev.data)) as { type?: string; payload?: unknown; ts?: string };
      if (!parsed?.type) return;
      if (parsed.type === 'ping') {
        try { socket.send(JSON.stringify({ type: 'pong' })); } catch { /* ignore */ }
        return;
      }
      if (parsed.type === 'hello') { lastEventAt = Date.now(); return; }
      const ts = parsed.ts ?? new Date().toISOString();
      emit(parsed.type as LiveEventType, (parsed.payload ?? {}) as LiveEventPayload<LiveEventType>, ts);
    } catch { /* ignore */ }
  });
}

/**
 * O que fazer com um 1008 — que pode ser "você saiu" ou "suas concessões
 * mudaram, reabra". Ver `CODIGO_SESSAO_INVALIDA`.
 *
 * Suspende o canal, pergunta, e só então decide:
 *
 * | resposta        | trava | status final   | por quê |
 * |-----------------|-------|----------------|---------|
 * | 401             | fica  | `unauthorized` | a sessão morreu mesmo |
 * | 200             | solta | reconecta      | era troca de concessões: reabrir COM as novas é o objetivo do servidor ao derrubar |
 * | rede/500        | solta | reconecta      | indisponibilidade momentânea não pode virar estado terminal |
 * | sem verificador | fica  | `unauthorized` | ninguém para perguntar nem para onde mandar o usuário; o rearme aqui é `refs → 0` |
 */
function tratarFechamentoPorSessao(): void {
  // Trava já no 1008, antes de perguntar: enquanto não se sabe, ninguém
  // reabre — nem esta rotina, nem uma tela que monte no meio.
  sessaoInvalida = true;
  // O `++` é defesa em profundidade e **não é coberto por teste**: hoje
  // duas verificações não se sobrepõem sem passar por `releaseConnection`
  // (que também fecha o ciclo), porque com a trava ligada nenhum socket
  // novo nasce para tomar um segundo 1008. Fica porque "esta resposta é da
  // minha pergunta" é propriedade da pergunta, não do logout. Não conte
  // como testada.
  const meuCiclo = ++cicloSessao;

  const verificar = verificarSessao;
  if (!verificar) {
    setStatus('unauthorized');
    return;
  }

  // Nem `open` (o socket morreu) nem `unauthorized` (ainda não se sabe).
  setStatus('verifying');

  const pergunta = Promise.resolve()
    .then(() => verificar())
    .catch((): ResultadoSessaoLive => 'indeterminada');

  // Corrida com o prazo. O `catch` acima cobre verificador que LANÇA; este
  // cobre verificador que nunca responde, que é outra falha.
  let prazo: ReturnType<typeof setTimeout> | undefined;
  const comPrazo = new Promise<ResultadoSessaoLive>((resolve) => {
    prazo = setTimeout(() => resolve('indeterminada'), TIMEOUT_VERIFICACAO_MS);
    void pergunta.then(resolve);
  });

  void comPrazo
    .then((resultado) => {
      // Resposta dentro do prazo: não deixa o temporizador para trás.
      clearTimeout(prazo);
      return resultado;
    })
    .then((resultado) => {
      // Resposta de OUTRA pergunta não decide nada. Dois casos reais: o
      // logout ocorrido com a pergunta em voo (o ciclo fechou em
      // `releaseConnection`), e a resposta atrasada de um ciclo anterior
      // chegando enquanto um novo 1008 está sendo verificado — sem a
      // guarda, ela soltaria a trava do ciclo novo.
      if (meuCiclo !== cicloSessao) return;

      if (resultado === 'invalida') {
        setStatus('unauthorized');
        return;
      }
      // `valida` e `indeterminada`: destrava e reabre. Pelo backoff, não na
      // hora — um servidor que derrube repetidas vezes não pode virar
      // tempestade de handshake, e o `open` bem-sucedido zera o backoff.
      sessaoInvalida = false;
      scheduleReconnect();
    });
}

function ensureConnected() {
  refs += 1;
  if (!ws && !reconnectTimer) connect();
}

function releaseConnection() {
  refs = Math.max(0, refs - 1);
  if (refs === 0) {
    closedByUser = true;
    // Fim do ciclo de sessão (shell desmontado por logout ou por 1008
    // confirmado): a trava sai daqui e não de um `reset()` público, para
    // que "destravar" seja consequência de não haver mais consumidor — e
    // não um botão que alguém possa apertar com a sessão ainda inválida.
    //
    // Este NÃO é mais o único caminho de destravamento: amarrar o rearme
    // só a `refs → 0` era o defeito, porque no gatilho mais comum do 1008
    // (admin muda o perfil) a sessão segue viva, o `AppShell` não desmonta
    // e `refs` nunca chega a zero — a aba ficava travada até o F5.
    sessaoInvalida = false;
    // Invalida verificação em voo: a resposta dela não deve reabrir nada.
    cicloSessao += 1;
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    if (ws) { try { ws.close(); } catch { /* ignore */ } }
    ws = null;
    setStatus('closed');
  }
}

/** Exposto só para teste (mesmo padrão de `_internals` do servidor).
 *  `status` é estado de módulo e só chega à UI pelo hook; o teste roda fora
 *  da árvore React e precisa de um espelho de leitura. */
export const _internals = {
  getStatus: (): LiveConnStatus => status,
  getRefs: (): number => refs,
  TIMEOUT_VERIFICACAO_MS,
};

// ------ Hooks públicos ------

/**
 * Assina um tipo de evento SEM ser um hook. Devolve a função de cancelamento.
 *
 * Existe porque há consumidores que só podem assinar condicionalmente — o
 * `useNavBadges` roda no `AppShell`, que chama o hook antes de checar se
 * existe sessão, e abrir `/ws/live` sem sessão só renderia um laço de
 * reconexão. Dentro de um efeito já condicionado, `subscribeLive` faz o que
 * o `useLiveEvent` faz (mesmo refcount, mesma conexão singleton) sem
 * violar a ordem de hooks.
 */
export function subscribeLive<T extends LiveEventType>(
  type: T,
  handler: (payload: LiveEventPayload<T>, ts: string) => void,
): () => void {
  const wrapper: AnyHandler = (p, ts) => handler(p as LiveEventPayload<T>, ts);
  let set = handlers.get(type);
  if (!set) { set = new Set(); handlers.set(type, set); }
  set.add(wrapper);
  ensureConnected();
  let cancelado = false;
  return () => {
    // Idempotente: cancelar duas vezes decrementaria o refcount duas vezes e
    // fecharia a conexão de quem ainda está usando.
    if (cancelado) return;
    cancelado = true;
    set!.delete(wrapper);
    releaseConnection();
  };
}

export function useLiveEvent<T extends LiveEventType>(
  type: T,
  handler: (payload: LiveEventPayload<T>, ts: string) => void,
): void {
  // Guarda referência estável ao handler mais recente para não re-subscrever
  // toda vez que o caller passar arrow inline.
  const ref = useRef(handler);
  useEffect(() => { ref.current = handler; }, [handler]);
  useEffect(
    () => subscribeLive(type, (p, ts) => ref.current(p as LiveEventPayload<T>, ts)),
    [type],
  );
}

export function useLiveConnection(): { status: LiveConnStatus; lastEventAt: number | null } {
  const [state, setState] = useState<{ status: LiveConnStatus; lastEventAt: number | null }>({ status, lastEventAt });
  useEffect(() => {
    const l: ConnListener = (s) => setState(s);
    statusListeners.add(l);
    ensureConnected();
    // Emite snapshot corrente pra alinhar com o hook novo
    l({ status, lastEventAt });
    return () => {
      statusListeners.delete(l);
      releaseConnection();
    };
  }, []);
  return state;
}
